/**
 * Feature flags — compile-time constants.
 *
 * Why constants and not runtime config: each flag below is treated as a
 * `const false`/`true` literal so the TypeScript compiler narrows
 * conditional branches and bundlers strip the gated code from the
 * production bundle. To restore a feature, flip the value here and ship
 * a new build — no env-var rollout, no AB framework needed.
 */
import { Platform } from 'react-native';

/**
 * AMB-228 — «Круги» (Circles) hidden from MVP.
 *
 * Set to `true` to instantly restore: tab «CIRCLES» in /chats, master-
 * profile circle tab, ActivityWall section, /community page, LiveDetail
 * EventThread + LiveCommentsPanel, /circle/:id deep links and routes,
 * AgapeCenter circle tab, FE-side announcement posting.
 *
 * Backend stays intact regardless — Stream channels and
 * `PostMasterEventMessageAsync` continue running. This flag only gates
 * frontend rendering and side-effects.
 */
export const CIRCLES_ENABLED = false;

/**
 * CORE-614 — live/room chat is served exclusively by the in-house main-API
 * SignalR hubs (ChatHub / WatchPartyHub → Postgres). The former
 * `live_stream_chat_via_stream` GetStream switch and its FE Stream path were
 * removed with the server-side Stream integration.
 */

/**
 * CORE-104 — «Чат сообщества мастера». BE side (CORE-96) shipped the
 * auto-join on subscribe, the `MasterCircleSummary.isMember` flag, and
 * a preview-messages endpoint so non-members see a teaser before they
 * subscribe. This FE flag gates the rendering of that surface on the
 * master profile (Dashboard view): a «Чат сообщества» tab that, for
 * members, mounts the existing chat panel, and for non-members shows
 * the last N messages from the preview endpoint plus a «Подписаться
 * чтобы писать» CTA.
 *
 * Independent from CIRCLES_ENABLED — the wider Круги surface stays
 * hidden behind that flag, but this single channel-per-master tab can
 * be flipped on its own once owner signs off on the UX. While `false`
 * the API client + types are still wired so the BE contract stays
 * type-checked and the flip is a one-line change.
 *
 * Rollout: owner approved for prod (CORE-104, 2026-07-17) → now ON everywhere.
 * Web picks it up on the dev→main promote + Vercel deploy; native needs a fresh
 * EAS build since the value is inlined at build time (won't reach already-
 * installed apps). Was `!IS_PROD_BUILD` (dev-only) until the sign-off.
 */
// CORE-901 — exported so the gamification QA preview can require a non-prod
// build before honouring the `?qa=gamification` URL override (on prod the only
// entry is the `gamification_preview` flag flip in the admin panel).
export const IS_PROD_BUILD = (process.env.EXPO_PUBLIC_API_URL || 'https://api.agapella.io').includes('api.agapella.io');
export const MASTER_COMMUNITY_CHAT_ENABLED = true;

/*
 * CORE-249 «Путь Света» used to live here as LIGHT_PATH_ENABLED = !IS_PROD_BUILD.
 * It moved to the runtime flag `light_path` (CORE-664) — see useLightPathEnabled
 * in hooks/useFeatureFlags.ts — so enabling gamification on prod is an admin-panel
 * flip instead of a web deploy plus an EAS rebuild.
 */

/**
 * CORE-396 (slice 4c) — render friend DMs through the backend-agnostic
 * AgapellaChatThread (via StreamChannelSource) instead of the stream-chat-react
 * <Chat> block. Dev-only while parity is finished (typing indicator, edit/delete
 * UI, non-image attachments still missing) so a routine dev→main promote can't
 * carry the half-done swap to prod. Flip the prod side on once validated.
 */
export const CHAT_DM_AGNOSTIC_ENABLED = !IS_PROD_BUILD;

/**
 * CORE-404 (slice 5d) — render CIRCLE chat through the backend-agnostic
 * AgapellaChatThread (via StreamChannelSource) instead of the stream-chat-react
 * <Chat> block. Parity is now done behind the flag: group thread + pinned bar
 * (5d-1), inline event cards + shared About/Events/EventThread panes (5d-2),
 * mute gate + block + quoted replies (5d-3), and a full composer — attachment
 * upload + voice recording, both voice-moderated (5d-4a/4b).
 *
 * Flipped to `!IS_PROD_BUILD` (dev on, prod off) — the same gate as the DM path.
 * NOTE: this is currently INERT because `CIRCLES_ENABLED` is false (AMB-228),
 * so no circle can be selected; it arms the agnostic path for when circles are
 * re-enabled on dev, at which point the flow can be verified in a browser. The
 * live proving ground meanwhile is dev DMs (CHAT_DM_AGNOSTIC_ENABLED), which
 * share this exact thread + composer. Deleting the <Chat> block stays blocked on
 * the prod AI / Wisdom-Council / DM flags (still dev-only).
 */
export const CHAT_CIRCLE_AGNOSTIC_ENABLED = !IS_PROD_BUILD;

/**
 * Wisdom Council characters (Buddha / Plato / Aristotle / Einstein) hidden while
 * the product is single-voice (Agapella only). The backend no longer addresses
 * these personas, so surfacing their cards/avatars would promise a mechanic that
 * isn't there. While `false`, the companion list is filtered to Agapella
 * (`sophia`/`isDefault`) — which removes the extra avatars from the members
 * roster, the welcome grid, and the council @-mention starters in one place.
 * Set to `true` to bring the council back (no other change needed).
 */
export const WISDOM_COUNCIL_ENABLED = false;

/**
 * CORE-496 — home-page onboarding, spoken by Agapella in her pop-up over a
 * dimmed home page.
 *
 * ON everywhere since CORE-501 (2026-08-03, owner sign-off on the copy): the
 * flag was `!IS_PROD_BUILD` on dev and a hard `false` on main while the ToV, the
 * «Проводники Света» wording and i18n were still the spec's draft. Now `true`
 * unconditionally, so dev, staging and prod all run the same flow — web only,
 * native ignores it. Set to `false` to pull it back everywhere at once.
 *
 * NOTE: the tour is delivered THROUGH the widget below, so App.tsx mounts
 * `AgapellaWidget` whenever this is on, regardless of AGAPELLA_WIDGET_ENABLED.
 */
export const ONBOARDING_TOUR_ENABLED = true;

/**
 * CORE-498/499 — persistent Agapella launcher + compact chat pop-up (AI
 * assistant widget). While `false` the bottom-right ✦ icon and its chat pop-up
 * are not rendered on their own. Flip to `false` only as a product call.
 *
 * `true`, not `!IS_PROD_BUILD` — reconciled with `main` (CORE-501 promote). The
 * widget has been live on prod since before the flag existed, so `!IS_PROD_BUILD`
 * here was a loaded gun: any wholesale dev→main promote evaluated it to `false`
 * on the prod build and would have silently switched the live widget off. Value
 * on dev is unchanged (`!IS_PROD_BUILD` was already `true` there) — this only
 * removes the promote trap. Turning the widget off is a separate product call.
 */
export const AGAPELLA_WIDGET_ENABLED = true;

/**
 * CORE-1157 — что мобильное приложение не показывает в релизе.
 *
 * Две поверхности целиком: «Программы» (создание, витрина, покупка,
 * прохождение) и денежный контур (Финансы, Фонд, кошелёк мастера, реквизиты
 * выплат, реферальные суммы). Обе живут и работают в вебе; на телефоне их
 * прячем до тех пор, пока платёжная часть не подтверждена для мобильных
 * сторов.
 *
 * Гейт, а не удаление кода: экраны, ручки и типы остаются на месте и
 * продолжают проверяться компилятором и тестами вместе с вебом. Вернуть
 * поверхность на телефон — поменять здесь `false` на `true` и собрать новый
 * билд.
 *
 * Гейт КОМПАЙЛ-ТАЙМОВЫЙ, а не рантайм-флаг из админки, и это главное:
 * `master_programs`, `fund_screen`, `referral_user_ui` нужны вебу, и однажды
 * их там включат. Если бы мобильное приложение слушало только их, тот же
 * тумблер открыл бы программы и деньги на телефоне — ровно то, что этот
 * тикет закрывает. Ниже платформа перекрывает флаг, а не наоборот.
 *
 * Бэкенд не трогается: ручки программ и покупок продолжают работать, доступ
 * купивших не меняется, письма и уведомления приходят как раньше.
 */
export const NATIVE_PROGRAMS_ENABLED = false;
export const NATIVE_MONEY_ENABLED = false;

/**
 * Показывать ли на этой платформе поверхность «Программы».
 *
 * Собрано здесь один раз, чтобы у каждого места вызова не было своего мнения
 * о том, что такое «натив»: экраны, роуты, deep links, вкладки, быстрые
 * действия и уведомления читают один и тот же ответ.
 */
export const PROGRAMS_SURFACE_ENABLED = Platform.OS === 'web' || NATIVE_PROGRAMS_ENABLED;

/**
 * Показывать ли на этой платформе денежные экраны: Финансы, Фонд и всё, что
 * из них растёт (распределение, прозрачность, реферальные начисления,
 * реквизиты выплат, кошелёк и продажи мастера).
 */
export const MONEY_SURFACE_ENABLED = Platform.OS === 'web' || NATIVE_MONEY_ENABLED;
